export type DownloadManagerDockCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export const DOWNLOAD_MANAGER_DOCK_STORAGE_KEY =
  'imai.download-manager.dock-corner.v1';

export type DownloadManagerDockPoint = {
  x: number;
  y: number;
};

export function hasExceededDownloadManagerDragDistance(
  start: DownloadManagerDockPoint,
  current: DownloadManagerDockPoint,
  threshold: number
): boolean {
  return (
    Math.hypot(current.x - start.x, current.y - start.y) >=
    toNonNegativeFinite(threshold)
  );
}

export type DownloadManagerDockViewport = {
  width: number;
  height: number;
};

export type DownloadManagerDockBounds = DownloadManagerDockViewport & {
  left: number;
  top: number;
};

export function getDownloadManagerRelativePanelCenter(
  panel: DownloadManagerDockBounds,
  boundary: DownloadManagerDockBounds
): DownloadManagerDockPoint {
  return {
    x: panel.left - boundary.left + panel.width / 2,
    y: panel.top - boundary.top + panel.height / 2,
  };
}

export type DownloadManagerDockSafeInsets = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

export type DownloadManagerDockTransformInput = {
  viewportWidth: number;
  viewportHeight: number;
  panelWidth: number;
  panelHeight: number;
  margin?: number;
  safeInsets?: DownloadManagerDockSafeInsets;
};

function toNonNegativeFinite(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, value)
    : 0;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function getAxisBounds(
  viewportSize: number,
  panelSize: number,
  leadingInset: number,
  trailingInset: number,
  margin: number
): { leading: number; trailing: number } {
  const leadingEdge = clamp(leadingInset + margin, 0, viewportSize);
  const trailingEdge = clamp(
    viewportSize - trailingInset - margin,
    0,
    viewportSize
  );
  const leading = Math.min(leadingEdge, trailingEdge);

  return {
    leading,
    trailing: Math.max(leading, trailingEdge - panelSize),
  };
}

export function getNearestDownloadManagerDockCorner(
  panelCenter: DownloadManagerDockPoint,
  viewport: DownloadManagerDockViewport
): DownloadManagerDockCorner {
  const midpointX = toNonNegativeFinite(viewport.width) / 2;
  const midpointY = toNonNegativeFinite(viewport.height) / 2;
  const isLeft = panelCenter.x <= midpointX;
  const isTop = panelCenter.y <= midpointY;

  if (isTop) return isLeft ? 'top-left' : 'top-right';
  return isLeft ? 'bottom-left' : 'bottom-right';
}

export function calculateDownloadManagerDockTransform(
  corner: DownloadManagerDockCorner,
  input: DownloadManagerDockTransformInput
): DownloadManagerDockPoint {
  const viewportWidth = toNonNegativeFinite(input.viewportWidth);
  const viewportHeight = toNonNegativeFinite(input.viewportHeight);
  const panelWidth = toNonNegativeFinite(input.panelWidth);
  const panelHeight = toNonNegativeFinite(input.panelHeight);
  const margin = toNonNegativeFinite(input.margin);
  const safeInsets = input.safeInsets;
  const horizontal = getAxisBounds(
    viewportWidth,
    panelWidth,
    toNonNegativeFinite(safeInsets?.left),
    toNonNegativeFinite(safeInsets?.right),
    margin
  );
  const vertical = getAxisBounds(
    viewportHeight,
    panelHeight,
    toNonNegativeFinite(safeInsets?.top),
    toNonNegativeFinite(safeInsets?.bottom),
    margin
  );

  return {
    x: corner.endsWith('left') ? horizontal.leading : horizontal.trailing,
    y: corner.startsWith('top') ? vertical.leading : vertical.trailing,
  };
}

export function parsePersistedDownloadManagerDockCorner(
  value: unknown
): DownloadManagerDockCorner | null {
  if (typeof value !== 'string') return null;

  switch (value) {
    case 'top-left':
    case 'top-right':
    case 'bottom-left':
    case 'bottom-right':
      return value;
    default:
      return null;
  }
}

export function getPersistedDownloadManagerDockCorner(
  value: unknown
): DownloadManagerDockCorner {
  return parsePersistedDownloadManagerDockCorner(value) ?? 'bottom-right';
}
